/* eslint-disable */
//! FIXME: Remove eslint-disable statements and reformat in typescript.

import jsPDF from "jspdf";
import { IPageBounds, IPdfCursor } from "../@types/Components";
import {
  DIN_A4_DIMENSIONS,
  FONT_TO_MM,
  FOOTER_SPACING_TOP,
  MASTER_PAGE_PADDING,
  PAGE_NUMBER_FONT_SIZE
} from "../utils/constants";

/**
 * This class is used by the export pdf function to keep track of:
 * - the position on the page and the current page number
 * - the remaining space on the page
 */
export class PdfCursor implements IPdfCursor {
  pageSize: IPageBounds;
  pagePadding: number;
  innerPageSize: IPageBounds;
  page: number;
  x: number;
  y: number;
  footerHeight: number;
  jsPdf: jsPDF;
  isPortrait: boolean;
  currentImageDimensions: IPageBounds;

  /**@constructor
   * @param {jsPDF} jsPDF
   * @param {boolean} isPortrait
   */
  constructor(jsPDF: jsPDF, isPortrait: boolean) {
    this.jsPdf = jsPDF;

    this.pageSize = {
      width: isPortrait ? DIN_A4_DIMENSIONS.width : DIN_A4_DIMENSIONS.height,
      height: isPortrait ? DIN_A4_DIMENSIONS.height : DIN_A4_DIMENSIONS.width,
    };

    this.pagePadding = MASTER_PAGE_PADDING;

    this.innerPageSize = {
      width: this.pageSize.width - this.pagePadding * 2,
      height: this.pageSize.height - this.pagePadding * 2,
    };
    this.isPortrait = isPortrait;

    this.page = 1;
    this.x = this.pagePadding;
    this.y = this.pagePadding;
    this.footerHeight =
      jsPDF.getTextDimensions("0", { fontSize: PAGE_NUMBER_FONT_SIZE }).h +
      FOOTER_SPACING_TOP;
    this.currentImageDimensions = { width: 0, height: 0 };
  }

  /**
   * Function to set the currentImageDimensions field of this class.
   * Will be used to calculate the resizing factor that may have been applied at the end of the exporting process.
   *
   * @param {IPageBounds} newCurrentDim the new width and height for the next image to print.
   */
  setImageDimensions(newCurrentDim: IPageBounds): void {
    this.currentImageDimensions = newCurrentDim;
  }

  /**
   * check if the given y position is below the page
   * @param {number} offset_y
   * @returns {boolean}
   */
  isBelowPage(offset_y: number): boolean {
    return this.getRemainingPageHeight(offset_y) < 0;
  }

  /**
   *  sets the cursor.x to 0 (plus padding)
   */
  resetX(): void {
    this.x = this.pagePadding;
  }

  /**
   * sets the cursor.y to 0 (plus padding)
   */
  resetY(): void {
    this.y = this.pagePadding;
  }

  /**
   * sets the cursor position to 0 (plus padding)
   */
  resetPos(): void {
    this.resetX();
    this.resetY();
  }

  /**
   * sets the cursor.y to the bottom of the page
   */
  setBottom() {
    this.y = this.pageSize.height - this.pagePadding;
  }

  /**
   * sets the cursor.x to the center of the page
   */
  centerX() {
    this.x = this.pageSize.width * 0.5;
  }

  getMaxX() {
    return this.pageSize.width - this.pagePadding; // or (innerPageSize + pagePadding)
  }

  /**
   * get the remaining space in height of the page (as float in millimeters)
   * @param {number} offset_y
   * @returns {number}
   */
  getRemainingPageHeight(offset_y: number): number {
    if (offset_y === undefined || offset_y === null) offset_y = 0;
    return (
      this.pageSize.height -
      this.pagePadding -
      this.footerHeight -
      (this.y + offset_y)
    );
  }

  /**
   * get the remaining space in width of the page (as float in millimeters)
   * @param {number} offset_x
   * @returns {number}
   */
  getRemainingPageWidth(offset_x: number): number {
    if (offset_x === undefined || offset_x === null) offset_x = 0;
    return this.pageSize.width - this.pagePadding - (this.x + offset_x);
  }

  /**
   * adds the height of one text line to the cursor.y
   * @param {number} fontSize
   */
  addFontHeight(fontSize: number): void {
    this.addHeight(fontSize * FONT_TO_MM); // font size to mm
  }

  /**
   * add height to the cursor.y
   * @param {number} addedHeight
   */
  addHeight(addedHeight: number): void {
    this.y += addedHeight;
    // todo: check space before adding content -> to prevent empty page at the end
    // if (this.getRemainingPageHeight() <= 0) {
    //     this.jsPDF.addPage("a4", this.orientation)
    //     // todo: add header at the top of every page after the first
    //     // todo: add footer with page number on every page
    //     // todo: pass onCreateNewPage lambda into cursor constructor, that creates header and footer on the pages
    //     this.page++
    //     this.x = this.pagePadding
    //     this.y = this.pagePadding
    // }
  }

  /**
   * add width to the cursor.x
   * @param {number} addedWidth
   */
  addWidth(addedWidth: number): void {
    this.x += addedWidth;
  }

  /**
   * call this whenever a new page is created
   * @param {Function} decoratePage_callback function to call to decorate the new page, null for no decoration
   */
  incrementPage(decoratePage_callback: () => {}): void {
    this.page++;
    this.x = this.pagePadding;
    this.y = this.pagePadding;
    if (decoratePage_callback !== null) {
      decoratePage_callback();
    }
    this.jsPdf.setPage(this.page);
  }

  // TODO: Move constants and color values to designated files!

  /**
   * Draws the position of the cursor for debugging.
   *
   * @param {string} color optional string color value that determines the drawn marker color.
   * @param {string} debugMsg optional string debgging message that will get printed to the console together with additional information.
   */
  debug(color: string = "#ff0000", debugMsg: string = ""): void {
    const rmbColor = this.jsPdf.getDrawColor();
    /* console.log(
      "PdfCursor.debug() - color: " +
        color +
        "\ncurrentPosition on the page:\n\t(x: " +
        this.x +
        "/y: " +
        this.y +
        ")\ndebugMessage:\n\t" +
        debugMsg,
    ); */
    this.jsPdf.setDrawColor(color);
    this.jsPdf.line(this.x - 2, this.y, this.x + 2, this.y);
    this.jsPdf.line(this.x, this.y - 2, this.x, this.y + 2);
    this.jsPdf.circle(this.x, this.y, 1);
    this.jsPdf.setDrawColor(rmbColor);
  }
}
